import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/Layout";
import { api } from "../lib/api";
import { fmtMoney, formatMarketValue } from "../lib/format";
import { X, ChevronRight, Users, Shield, Wind, Crosshair, HandMetal, Search, TrendingUp, TrendingDown, Minus } from "lucide-react";

// FIFA-style position groups. Free-text variants from the DB ("Centre Back",
// "Right Winger", etc.) are normalised onto these buckets via _bucketFor below.
const GROUPS = [
  { key: "GK", label: "Goalkeepers", short: "GK", color: "amber",   icon: HandMetal },
  { key: "DF", label: "Defenders",   short: "DEF", color: "blue",    icon: Shield },
  { key: "MF", label: "Midfielders", short: "MID", color: "emerald", icon: Users },
  { key: "WG", label: "Wingers",     short: "WIN", color: "violet",  icon: Wind },
  { key: "FW", label: "Forwards",    short: "FWD", color: "rose",    icon: Crosshair },
];

const GROUP_TONES = {
  amber:   { card: "from-amber-50 to-amber-100 border-amber-200",      text: "text-amber-800",      stat: "text-amber-900",   ring: "ring-amber-300" },
  blue:    { card: "from-blue-50 to-blue-100 border-blue-200",         text: "text-blue-800",       stat: "text-blue-900",    ring: "ring-blue-300" },
  emerald: { card: "from-emerald-50 to-emerald-100 border-emerald-200",text: "text-emerald-800",    stat: "text-emerald-900", ring: "ring-emerald-300" },
  violet:  { card: "from-violet-50 to-violet-100 border-violet-200",   text: "text-violet-800",     stat: "text-violet-900",  ring: "ring-violet-300" },
  rose:    { card: "from-rose-50 to-rose-100 border-rose-200",         text: "text-rose-800",       stat: "text-rose-900",    ring: "ring-rose-300" },
};

// ── 4-3-3 depth-chart bucketer ───────────────────────────────────────────
// Maps a free-text position to one of the nine logical 4-3-3 slots
// (GK · LB · CB · RB · CDM · CM · LW · ST · RW). The visual 11-spot 4-3-3
// re-uses CB twice (LCB+RCB) and CM twice (LCM+RCM), but both share the
// same logical bucket and therefore the same player count + drawer list.
function _slotFor(rawPos) {
  if (!rawPos) return null;
  const p = rawPos.toString().toUpperCase().replace(/\s+/g, " ").trim();
  if (/^GK\b|GOAL\s*KEEPER|KEEPER/.test(p)) return "GK";
  if (/^LB\b|^LWB\b|LEFT\s+(BACK|FULL ?BACK|WING ?BACK)/.test(p)) return "LB";
  if (/^RB\b|^RWB\b|RIGHT\s+(BACK|FULL ?BACK|WING ?BACK)/.test(p)) return "RB";
  if (/^CB\b|^[LR]CB\b|CENTRE\s*BACK|CENTER\s*BACK|^CENTRAL\s+DEFEND|^DEFEND/.test(p)) return "CB";
  if (/^CDM\b|^DM\b|HOLDING|DEFENSIVE\s+MID(FIELDER)?/.test(p)) return "CDM";
  if (/^CAM\b|^AM\b|^CM\b|^[LR]CM\b|ATTACK(ING)?\s+MID|CENT(RE|ER)\s+MID|MIDFIELDER/.test(p)) return "CM";
  if (/^LW\b|LEFT\s+(WINGER|WING|MID)|^LM\b/.test(p)) return "LW";
  if (/^RW\b|RIGHT\s+(WINGER|WING|MID)|^RM\b/.test(p)) return "RW";
  if (/^ST\b|^CF\b|^SS\b|STRIKER|FORWARD/.test(p)) return "ST";
  if (/WINGER/.test(p)) return "LW";  // unspecified-side winger fallback
  return null;
}

function _bucketFor(rawPos) {
  if (!rawPos) return null;
  const p = rawPos.toString().toUpperCase().replace(/\s+/g, " ").trim();
  if (/^GK\b|GOAL\s*KEEPER|KEEPER/.test(p)) return "GK";
  if (/^(C|L|R)?B\b|BACK\b|DEFEND/.test(p)) return "DF";
  if (/WING|^LW\b|^RW\b|^LM\b|^RM\b/.test(p)) return "WG";
  if (/MIDFIELD|^CM\b|^CDM\b|^CAM\b|^DM\b|^AM\b/.test(p)) return "MF";
  if (/STRIK|FORWARD|^ST\b|^CF\b|^SS\b/.test(p)) return "FW";
  return null;
}

function valueOf(c) {
  // Single source of truth: Transfermarkt integration_cache (exposed as
  // market_value_eur on the client doc). Fall back to legacy fields only
  // when the cache lookup hasn't run yet.
  return Number(
    c.market_value_eur || c.current_value_eur || c.estimated_value_eur || 0,
  ) || 0;
}

// SVG sparkline — draws a polyline of historical values + an area fill.
// Pass `points` (array of numbers) and an optional `color` token (tailwind
// hue) plus the up/down trend flag for accent strokes.
function Sparkline({ points, trend = "flat", height = 36 }) {
  if (!points || points.length < 2) {
    return <div className="h-9 text-[10px] text-slate-400 italic flex items-center">No history</div>;
  }
  const width = 140;
  const max = Math.max(...points, 1);
  const min = Math.min(...points, 0);
  const range = Math.max(max - min, 1);
  const xs = (i) => (points.length === 1 ? width / 2 : (i / (points.length - 1)) * (width - 4) + 2);
  const ys = (v) => height - 2 - ((v - min) / range) * (height - 4);
  const path = points.map((v, i) => `${i === 0 ? "M" : "L"} ${xs(i).toFixed(1)} ${ys(v).toFixed(1)}`).join(" ");
  const area = `${path} L ${xs(points.length - 1).toFixed(1)} ${height} L ${xs(0).toFixed(1)} ${height} Z`;
  const stroke = trend === "up" ? "#059669" : trend === "down" ? "#dc2626" : "#64748b";
  const fill = trend === "up" ? "url(#sparkUp)" : trend === "down" ? "url(#sparkDown)" : "url(#sparkFlat)";
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full" style={{ height }} aria-hidden>
      <defs>
        <linearGradient id="sparkUp" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
          <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="sparkDown" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#ef4444" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#ef4444" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="sparkFlat" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#94a3b8" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#94a3b8" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill={fill} stroke="none" />
      <path d={path} fill="none" stroke={stroke} strokeWidth={1.6} strokeLinejoin="round" strokeLinecap="round" />
      {/* Endpoint dot */}
      <circle
        cx={xs(points.length - 1)}
        cy={ys(points[points.length - 1])}
        r="2.4"
        fill={stroke}
      />
    </svg>
  );
}

function trendDirection(deltaEur) {
  if (!deltaEur) return "flat";
  if (deltaEur > 0) return "up";
  return "down";
}

function fmtDelta(eur) {
  if (!eur) return "Flat";
  const sign = eur > 0 ? "+" : "−";
  const abs = Math.abs(eur);
  if (abs >= 1_000_000) return `${sign}€${(abs / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${sign}€${(abs / 1_000).toFixed(0)}K`;
  return `${sign}€${abs.toFixed(0)}`;
}

export default function PositionMapAgent() {
  const [clients, setClients] = useState(null);
  const [trends, setTrends] = useState(null);
  const [openGroup, setOpenGroup] = useState(null); // group key
  const [q, setQ] = useState("");
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/clients")
      .then((r) => {
        const arr = Array.isArray(r.data) ? r.data : r.data?.clients || [];
        setClients(arr);
      })
      .catch((e) => setErr(e?.response?.data?.detail || "Could not load roster"));
    api.get("/position-map/agent/trends")
      .then((r) => setTrends(r.data))
      .catch(() => setTrends(null));
  }, []);

  const grouped = useMemo(() => {
    const buckets = Object.fromEntries(GROUPS.map((g) => [g.key, []]));
    const unmatched = [];
    (clients || []).forEach((c) => {
      const bk = _bucketFor(c.position);
      if (bk) buckets[bk].push(c);
      else unmatched.push(c);
    });
    return { buckets, unmatched };
  }, [clients]);

  const totalValue = useMemo(
    () => (clients || []).reduce((acc, c) => acc + valueOf(c), 0),
    [clients],
  );

  if (clients === null) {
    return <Layout title="Position map" subtitle="Roster grouped by position"><div className="text-sm text-slate-500">Loading roster…</div></Layout>;
  }

  return (
    <Layout title="Position map" subtitle="Your roster — grouped by position">
      {err && <div className="text-sm font-bold text-rose-700 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2 mb-4" data-testid="position-map-error">{err}</div>}

      {/* Roster totals */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="pm-card p-5 bg-gradient-to-br from-slate-50 to-emerald-50 border-emerald-200" data-testid="roster-total-value-card">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-emerald-700">Total roster value</div>
          <div className="font-display text-4xl font-black tracking-tight text-slate-900 mt-1" data-testid="roster-total-value">{fmtMoney(totalValue)}</div>
          <div className="text-[11px] text-slate-500 mt-1">{(clients || []).length} player{clients.length === 1 ? "" : "s"} on the books</div>
          {trends && trends.total_delta_12mo_eur !== 0 && (
            <div
              className={`text-[11px] font-bold mt-2 inline-flex items-center gap-1 ${
                trends.total_delta_12mo_eur > 0 ? "text-emerald-700" : "text-rose-700"
              }`}
              data-testid="roster-total-delta"
            >
              {trends.total_delta_12mo_eur > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
              {fmtDelta(trends.total_delta_12mo_eur)} ({trends.total_delta_12mo_pct >= 0 ? "+" : ""}{trends.total_delta_12mo_pct.toFixed(1)}%) vs last year
            </div>
          )}
        </div>
        <div className="pm-card p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Players with value</div>
          <div className="font-display text-4xl font-black tracking-tight text-slate-900 mt-1" data-testid="roster-with-value">
            {clients.filter((c) => valueOf(c) > 0).length}
          </div>
          <div className="text-[11px] text-slate-500 mt-1">remaining are listed without a Transfermarkt value</div>
        </div>
        <div className="pm-card p-5">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Top earner</div>
          {(() => {
            const top = [...clients].sort((a, b) => valueOf(b) - valueOf(a))[0];
            if (!top || valueOf(top) === 0) {
              return <div className="font-display text-2xl font-black tracking-tight text-slate-900 mt-1">—</div>;
            }
            return (
              <Link to={`/clients/${top.id}`} className="block mt-1" data-testid="roster-top-earner">
                <div className="font-display text-2xl font-black tracking-tight text-slate-900 truncate">{top.full_name}</div>
                <div className="text-[11px] text-slate-500 mt-0.5">{top.position} · {formatMarketValue(valueOf(top))}</div>
              </Link>
            );
          })()}
        </div>
      </div>

      {/* Formation pitch — every player placed on the grass at their position */}
      <FormationPitch clients={clients} unmatched={grouped.unmatched} />

      {/* Position group cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4" data-testid="position-groups">
        {GROUPS.map((g) => {
          const players = grouped.buckets[g.key];
          const groupValue = players.reduce((acc, c) => acc + valueOf(c), 0);
          const tones = GROUP_TONES[g.color];
          const Icon = g.icon;
          const trend = trends?.groups?.find((t) => t.key === g.key);
          const series = (trend?.points || []).map((p) => p.value_eur);
          const dir = trendDirection(trend?.delta_12mo_eur || 0);
          return (
            <button
              key={g.key}
              type="button"
              onClick={() => setOpenGroup(g.key)}
              className={`pm-card pm-card-hover p-5 text-left bg-gradient-to-br ${tones.card} border transition-transform hover:-translate-y-0.5`}
              data-testid={`position-group-${g.key}`}
            >
              <div className="flex items-center justify-between mb-3">
                <div className={`w-10 h-10 rounded-xl bg-white/80 backdrop-blur flex items-center justify-center ${tones.text}`}>
                  <Icon size={18} />
                </div>
                <span className={`text-[10px] uppercase tracking-[0.22em] font-bold ${tones.text}`}>{g.short}</span>
              </div>
              <div className={`text-[10px] uppercase tracking-[0.18em] font-bold ${tones.text} opacity-80`}>{g.label}</div>
              <div className={`font-display text-2xl font-black tracking-tight ${tones.stat} mt-1`} data-testid={`position-group-${g.key}-value`}>
                {fmtMoney(groupValue)}
              </div>
              <div className="text-[11px] text-slate-700 font-bold mt-1.5 flex items-center justify-between">
                <span data-testid={`position-group-${g.key}-count`}>{players.length} player{players.length === 1 ? "" : "s"}</span>
                <ChevronRight size={12} className="opacity-50" />
              </div>

              {/* Trend sparkline + delta vs last year (from Transfermarkt history) */}
              {trend && series.length >= 2 && (
                <div className="mt-3 pt-3 border-t border-white/60" data-testid={`position-group-${g.key}-trend`}>
                  <Sparkline points={series} trend={dir} height={32} />
                  <div className="flex items-center justify-between mt-1">
                    <span
                      className={`text-[11px] font-bold inline-flex items-center gap-0.5 ${
                        dir === "up" ? "text-emerald-700" : dir === "down" ? "text-rose-700" : "text-slate-500"
                      }`}
                      data-testid={`position-group-${g.key}-delta`}
                    >
                      {dir === "up" && <TrendingUp size={11} />}
                      {dir === "down" && <TrendingDown size={11} />}
                      {dir === "flat" && <Minus size={11} />}
                      {fmtDelta(trend.delta_12mo_eur)}
                      {trend.delta_12mo_pct !== 0 && (
                        <span className="opacity-75">({trend.delta_12mo_pct > 0 ? "+" : ""}{trend.delta_12mo_pct.toFixed(1)}%)</span>
                      )}
                    </span>
                    <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">12 mo</span>
                  </div>
                </div>
              )}

              {/* Top 3 avatar stack */}
              {players.length > 0 && (
                <div className="flex -space-x-2 mt-3" data-testid={`position-group-${g.key}-avatars`}>
                  {players.slice(0, 4).map((p) => (
                    <div
                      key={p.id}
                      title={p.full_name}
                      className="w-7 h-7 rounded-full bg-white border-2 border-white shadow flex items-center justify-center text-[10px] font-bold text-slate-700 overflow-hidden"
                    >
                      {p.photo_url ? <img src={p.photo_url} alt="" className="w-full h-full object-cover" /> : (p.full_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                    </div>
                  ))}
                  {players.length > 4 && (
                    <div className="w-7 h-7 rounded-full bg-slate-900 border-2 border-white shadow flex items-center justify-center text-[10px] font-bold text-white">
                      +{players.length - 4}
                    </div>
                  )}
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Drilldown panel */}
      {openGroup && (
        <GroupDrawer
          group={GROUPS.find((g) => g.key === openGroup)}
          players={grouped.buckets[openGroup]}
          trend={trends?.groups?.find((t) => t.key === openGroup)}
          q={q}
          setQ={setQ}
          onClose={() => { setOpenGroup(null); setQ(""); }}
        />
      )}
    </Layout>
  );
}

function GroupDrawer({ group, players, trend, q, setQ, onClose }) {
  const tones = GROUP_TONES[group.color];
  const groupValue = players.reduce((acc, c) => acc + valueOf(c), 0);
  const filtered = !q.trim()
    ? players
    : players.filter((p) => (p.full_name || "").toLowerCase().includes(q.trim().toLowerCase()));
  const sorted = [...filtered].sort((a, b) => valueOf(b) - valueOf(a));
  const Icon = group.icon;

  return (
    <div className="fixed inset-0 z-[60] flex items-stretch justify-end bg-black/50" onClick={onClose} data-testid="position-group-drawer">
      <div
        className="bg-white shadow-2xl w-full max-w-2xl h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-6 py-5 bg-gradient-to-br ${tones.card} border-b sticky top-0 z-10`}>
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="flex items-center gap-3">
              <div className={`w-12 h-12 rounded-xl bg-white/80 flex items-center justify-center ${tones.text}`}>
                <Icon size={20} />
              </div>
              <div>
                <div className={`text-[10px] uppercase tracking-[0.22em] font-bold ${tones.text} opacity-80`}>{group.short}</div>
                <div className="font-display text-2xl font-black tracking-tight">{group.label}</div>
              </div>
            </div>
            <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="position-group-drawer-close">
              <X size={18} />
            </button>
          </div>
          <div className="flex items-baseline gap-4">
            <div>
              <div className={`text-[10px] uppercase tracking-[0.22em] font-bold ${tones.text} opacity-80`}>Group value</div>
              <div className={`font-display text-3xl font-black tracking-tight ${tones.stat}`} data-testid="position-group-drawer-total">
                {fmtMoney(groupValue)}
              </div>
            </div>
            <div className="border-l border-slate-300/60 pl-4">
              <div className={`text-[10px] uppercase tracking-[0.22em] font-bold ${tones.text} opacity-80`}>Players</div>
              <div className="font-display text-3xl font-black tracking-tight text-slate-900" data-testid="position-group-drawer-count">
                {players.length}
              </div>
            </div>
            {trend && trend.points && trend.points.length >= 2 && (
              <div className="border-l border-slate-300/60 pl-4 flex-1 min-w-0" data-testid="position-group-drawer-trend">
                <div className={`text-[10px] uppercase tracking-[0.22em] font-bold ${tones.text} opacity-80 mb-1`}>12-month trend</div>
                <Sparkline points={trend.points.map((p) => p.value_eur)} trend={trendDirection(trend.delta_12mo_eur || 0)} height={32} />
                <div
                  className={`text-[11px] font-bold mt-0.5 inline-flex items-center gap-1 ${
                    (trend.delta_12mo_eur || 0) > 0 ? "text-emerald-700" : (trend.delta_12mo_eur || 0) < 0 ? "text-rose-700" : "text-slate-500"
                  }`}
                  data-testid="position-group-drawer-delta"
                >
                  {(trend.delta_12mo_eur || 0) > 0 && <TrendingUp size={11} />}
                  {(trend.delta_12mo_eur || 0) < 0 && <TrendingDown size={11} />}
                  {!(trend.delta_12mo_eur || 0) && <Minus size={11} />}
                  {fmtDelta(trend.delta_12mo_eur || 0)}
                  {trend.delta_12mo_pct !== 0 && (
                    <span className="opacity-75">({trend.delta_12mo_pct > 0 ? "+" : ""}{trend.delta_12mo_pct.toFixed(1)}%)</span>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Search */}
        <div className="px-6 pt-5 pb-2">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder={`Search ${group.label.toLowerCase()}…`}
              className="w-full h-10 pl-9 pr-3 rounded-lg border border-slate-200 text-sm"
              data-testid="position-group-drawer-search"
            />
          </div>
        </div>

        {/* Player list */}
        <div className="px-6 py-4 space-y-2">
          {sorted.length === 0 && (
            <div className="text-sm text-slate-500 italic py-6 text-center" data-testid="position-group-drawer-empty">
              No players match.
            </div>
          )}
          {sorted.map((p) => (
            <Link
              key={p.id}
              to={`/clients/${p.id}`}
              className="flex items-center gap-3 p-3 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors"
              data-testid={`position-group-drawer-player-${p.id}`}
            >
              <div className="w-12 h-12 rounded-full bg-slate-100 overflow-hidden shrink-0 flex items-center justify-center">
                {p.photo_url ? (
                  <img src={p.photo_url} alt="" className="w-full h-full object-cover" />
                ) : (
                  <span className="font-display font-black text-slate-600">{(p.full_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-slate-900 truncate">{p.full_name}</div>
                <div className="text-[11px] text-slate-500 truncate flex items-center gap-1.5">
                  <span>{p.position || "—"}</span>
                  {p.current_club && <><span>·</span><span>{p.current_club}</span></>}
                  {p.nationality && <><span>·</span><span>{p.nationality}</span></>}
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="font-mono-tab font-black text-sm text-slate-900">
                  {formatMarketValue(valueOf(p))}
                </div>
                <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400">Market value</div>
              </div>
              <ChevronRight size={14} className="text-slate-300 shrink-0" />
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}


// ── Formation pitch ─────────────────────────────────────────────────────
// Renders the standard 4-3-3 depth chart. Each formation slot shows
// the position label + a count badge of how many roster players fit it.
// Clicking a slot opens a drawer with the player list (sorted by value).
// CB1/CB2 share the "CB" bucket, LCM/RCM share "CM" — the visual symmetry
// of 4-3-3 is preserved without double-counting the underlying players.

const FORMATION_433 = [
  // Forwards row (top of pitch)
  { id: "LW",  label: "LW",  bucket: "LW",  x: 18, y: 18 },
  { id: "ST",  label: "ST",  bucket: "ST",  x: 50, y: 14 },
  { id: "RW",  label: "RW",  bucket: "RW",  x: 82, y: 18 },
  // Midfield row
  { id: "LCM", label: "CM",  bucket: "CM",  x: 30, y: 43 },
  { id: "CDM", label: "CDM", bucket: "CDM", x: 50, y: 50 },
  { id: "RCM", label: "CM",  bucket: "CM",  x: 70, y: 43 },
  // Defence row
  { id: "LB",  label: "LB",  bucket: "LB",  x: 12, y: 70 },
  { id: "LCB", label: "CB",  bucket: "CB",  x: 36, y: 73 },
  { id: "RCB", label: "CB",  bucket: "CB",  x: 64, y: 73 },
  { id: "RB",  label: "RB",  bucket: "RB",  x: 88, y: 70 },
  // Goalkeeper
  { id: "GK",  label: "GK",  bucket: "GK",  x: 50, y: 92 },
];

const BUCKET_COLORS = {
  GK:  "#f59e0b",
  LB:  "#3b82f6", CB: "#3b82f6", RB: "#3b82f6",
  CDM: "#10b981", CM: "#10b981",
  LW:  "#8b5cf6", RW: "#8b5cf6",
  ST:  "#f43f5e",
};

const BUCKET_LABELS = {
  GK: "Goalkeepers", LB: "Left backs", CB: "Centre backs", RB: "Right backs",
  CDM: "Defensive midfielders", CM: "Central midfielders",
  LW: "Left wingers", RW: "Right wingers", ST: "Strikers",
};

function FormationPitch({ clients, unmatched }) {
  // Group every roster player into one of the 9 logical 4-3-3 buckets.
  // Players whose position can't be mapped fall into `unmatched` and are
  // rendered as pills below the pitch.
  const playersByBucket = useMemo(() => {
    const out = { GK: [], LB: [], CB: [], RB: [], CDM: [], CM: [], LW: [], RW: [], ST: [] };
    (clients || []).forEach((c) => {
      const slot = _slotFor(c.position);
      if (slot && out[slot]) out[slot].push(c);
    });
    return out;
  }, [clients]);
  const [openSlot, setOpenSlot] = useState(null);
  const openPlayers = openSlot ? playersByBucket[openSlot] : [];
  return (
    <div className="pm-card p-4 mb-6" data-testid="position-map-pitch-wrap">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">4-3-3 depth chart</div>
          <div className="font-display text-lg font-bold tracking-tight">Position map</div>
        </div>
        <div className="text-[11px] text-slate-500">Click a position to see the players</div>
      </div>
      <div
        className="relative w-full rounded-xl overflow-hidden border border-emerald-800/30 shadow-inner"
        style={{
          background: "linear-gradient(180deg, #0a5d2e 0%, #0e6b35 50%, #0a5d2e 100%)",
          aspectRatio: "1.05/1",
        }}
        data-testid="position-map-pitch"
      >
        {/* Pitch lines */}
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full opacity-40">
          <rect x="1" y="1" width="98" height="98" fill="none" stroke="white" strokeWidth="0.3" />
          <line x1="1" y1="50" x2="99" y2="50" stroke="white" strokeWidth="0.3" />
          <circle cx="50" cy="50" r="8" fill="none" stroke="white" strokeWidth="0.3" />
          <circle cx="50" cy="50" r="0.5" fill="white" />
          <rect x="28" y="1" width="44" height="11" fill="none" stroke="white" strokeWidth="0.3" />
          <rect x="40" y="1" width="20" height="5" fill="none" stroke="white" strokeWidth="0.3" />
          <rect x="28" y="88" width="44" height="11" fill="none" stroke="white" strokeWidth="0.3" />
          <rect x="40" y="94" width="20" height="5" fill="none" stroke="white" strokeWidth="0.3" />
        </svg>
        {/* 11 4-3-3 pucks */}
        {FORMATION_433.map((slot) => {
          const count = (playersByBucket[slot.bucket] || []).length;
          const color = BUCKET_COLORS[slot.bucket] || "#64748b";
          const isHot = count > 0;
          return (
            <button
              key={slot.id}
              type="button"
              onClick={() => isHot && setOpenSlot(slot.bucket)}
              disabled={!isHot}
              className={`absolute -translate-x-1/2 -translate-y-1/2 transition-transform ${isHot ? "hover:scale-110 cursor-pointer" : "cursor-default opacity-70"}`}
              style={{ left: `${slot.x}%`, top: `${slot.y}%` }}
              data-testid={`formation-slot-${slot.id}`}
              title={`${slot.label} — ${count} player${count === 1 ? "" : "s"}`}
            >
              <div className="relative">
                <div
                  className={`w-16 h-16 rounded-full border-[3px] border-white shadow-2xl flex flex-col items-center justify-center ${isHot ? "" : "opacity-60"}`}
                  style={{ background: isHot ? color : "rgba(15,23,42,0.6)" }}
                >
                  <span className="text-white text-[9px] font-bold tracking-wider uppercase leading-none">{slot.label}</span>
                  <span className="text-white font-display font-black text-2xl leading-none mt-0.5" data-testid={`formation-slot-count-${slot.id}`}>
                    {count}
                  </span>
                </div>
                {count > 1 && (
                  <span className="absolute -top-1 -right-1 px-1.5 h-4 rounded-full bg-white text-[9px] font-bold text-slate-900 flex items-center justify-center shadow">
                    ×{count}
                  </span>
                )}
              </div>
            </button>
          );
        })}
      </div>
      {/* Unclassified — directly below the pitch */}
      {unmatched.length > 0 && (
        <div className="mt-4 pt-4 border-t border-slate-200" data-testid="pitch-unclassified">
          <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500 mb-2">
            Unclassified positions ({unmatched.length})
          </div>
          <div className="flex flex-wrap gap-2">
            {unmatched.map((p) => (
              <Link
                key={p.id}
                to={`/clients/${p.id}`}
                className="pm-pill pm-pill-slate text-[11px] hover:bg-slate-200"
                data-testid={`pitch-unclassified-${p.id}`}
              >
                {p.full_name} · {p.position || "no position"}
              </Link>
            ))}
          </div>
        </div>
      )}
      {/* Slot drawer */}
      {openSlot && (
        <div
          className="fixed inset-0 z-[70] flex items-stretch justify-end bg-black/50"
          onClick={() => setOpenSlot(null)}
          data-testid="formation-slot-drawer"
        >
          <div
            className="bg-white shadow-2xl w-full max-w-md h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div
              className="px-6 py-5 border-b sticky top-0 z-10"
              style={{ background: `linear-gradient(135deg, ${BUCKET_COLORS[openSlot]}22 0%, ${BUCKET_COLORS[openSlot]}11 100%)` }}
            >
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-slate-500">Depth chart</div>
                  <div className="font-display text-2xl font-black tracking-tight">{BUCKET_LABELS[openSlot]}</div>
                  <div className="text-[11px] text-slate-500 mt-1">
                    {openPlayers.length} player{openPlayers.length === 1 ? "" : "s"} on this position
                  </div>
                </div>
                <button
                  onClick={() => setOpenSlot(null)}
                  className="text-slate-500 hover:text-slate-900"
                  data-testid="formation-slot-drawer-close"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="p-4 space-y-2">
              {openPlayers.length === 0 ? (
                <div className="text-sm text-slate-500 italic">No players on this position.</div>
              ) : (
                [...openPlayers].sort((a, b) => valueOf(b) - valueOf(a)).map((p) => (
                  <Link
                    key={p.id}
                    to={`/clients/${p.id}`}
                    className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-slate-50 transition-colors border border-slate-200"
                    data-testid={`formation-drawer-player-${p.id}`}
                    onClick={() => setOpenSlot(null)}
                  >
                    {p.photo_url ? (
                      <img src={p.photo_url} alt="" className="w-10 h-10 rounded-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center font-bold text-slate-600 text-xs">
                        {(p.full_name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("")}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-slate-900 truncate">{p.full_name}</div>
                      <div className="text-[11px] text-slate-500 truncate">
                        {p.position || "—"}{p.current_club ? ` · ${p.current_club}` : ""}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div className="font-mono-tab font-black text-sm text-slate-900">
                        {formatMarketValue(valueOf(p))}
                      </div>
                      <div className="text-[9px] uppercase tracking-wider font-bold text-slate-400">Market value</div>
                    </div>
                    <ChevronRight size={14} className="text-slate-300 shrink-0" />
                  </Link>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
